import { RootState } from 'MyTypes';
import { isEmpty, pick } from 'lodash/fp';

import { AiChatFeedbackQueryParams, PaginationQueryParams } from '@portal/common/types';
import { serializeDateShort } from '@portal/common/utility/api-helpers';

import config from '../../config';
import { getDatasets } from '../data/selectors';
import { AnalyticsAPIParams, ChartType, DeliveryMethod, EventType } from './models';
import { getInsightsReportingParams, getInsightsSelectedFilters } from './selectors';

const AI_LIST_PARAMS: (keyof AiChatFeedbackQueryParams)[] = [
    'page',
    'per_page',
    'search',
    'reaction',
];

const unfoldDataQueryArrayConfig = {
    queried_data_types: true,
    visible_filters: true,
    round_id: true,
    cause_id: true,
    sequela_id: true,
    rei_id: true,
    covariate_id: true,
    sdoh_id: true,
    location_id: true,
    age_group_id: true,
    gender_id: true,
    measure_id: true,
    metric_id: true,
    forecast_scenario_id: true,
    year: true,
    risk_exposure_id: true,
    pathogen_id: true,
    infectious_syndrome_id: true,
    counterfactual_id: true,
    antibiotic_class_id: true,
    multiple_myeloma_treatment_id: true,
    race_id: true,
    vehss_condition_id: true,
    education_id: true,
};

const unfoldArrayConfig = {
    [EventType.dataQuery]: unfoldDataQueryArrayConfig,
    [EventType.dataExport]: {
        ...unfoldDataQueryArrayConfig,
        chart_types: true,
    },
    [EventType.pdfExport]: {
        ...unfoldDataQueryArrayConfig,
        chart_types: true,
        pdf_report_options: true,
    },
    [EventType.fileDownload]: {},
    [EventType.pageView]: {},
    [EventType.visit]: {},
    [EventType.userFeedback]: {},
    [EventType.conditionSearch]: {
        cause_id: true,
        sequela_id: true,
        rei_id: true,
        cro_id: true,
        pathogen_id: true,
        risk_exposure_id: true,
        covariate_id: true,
    },
};

export const generateAnalyticsAPIParams = (
    state: RootState,
    dataTableQueryParams?: PaginationQueryParams
) => {
    const { eventType } = state.insights;
    const filters = getInsightsSelectedFilters(state);
    const {
        fromDate,
        toDate,
        timeUnit,
        aggregationType,
        aggregationValue,
        organizations,
        groupBy,
        dataTableColumns,
        chartType,
        dataProjectId,
        dataTableParams,
        deliveryMethod,
    } = filters;
    const { organizations: allOrganizations } = state.data;
    const params: AnalyticsAPIParams = {};
    if (fromDate) {
        params.from = serializeDateShort(fromDate);
    }
    if (toDate) {
        params.to = serializeDateShort(toDate);
    }
    if (!isEmpty(organizations)) {
        params.organization_id = organizations.join(',');
    } else if (!isEmpty(allOrganizations)) {
        // NOTE: when selected all organizations the data request doesn't include some organizations based on config variable
        params.organization_id = allOrganizations
            .map(({ id }) => id)
            .filter((id) => !config.orgsExcludeFromAllOrgsAnalytics.includes(id))
            .join(',');
    }
    if (timeUnit) {
        params.aggregation_period = timeUnit;
    }
    if (aggregationType) {
        params.aggregation_function = aggregationType; // aggr type
        params.aggregation_value = aggregationValue || undefined;
    }
    if (!(isEmpty(groupBy) && isEmpty(dataTableColumns))) {
        let groupByColumns = groupBy;
        if (chartType === ChartType.table) {
            const datasets = getDatasets(state);
            const acceptableParams = getInsightsReportingParams(eventType, datasets, dataProjectId);
            const availableColumns = dataTableColumns.filter((column) =>
                acceptableParams.includes(column)
            );
            groupByColumns = [...groupBy, ...availableColumns];
        }
        params.group_by = groupByColumns.join(',');
        params.unfold_array =
            groupByColumns.filter((param) => unfoldArrayConfig[eventType][param]).join(',') ||
            undefined;
    }
    if (!isEmpty(dataProjectId)) {
        params.data_project_id = dataProjectId.toString();
    }
    if (eventType === EventType.pdfExport) {
        params.delivery_method = deliveryMethod || DeliveryMethod.download;
    }
    if (chartType === ChartType.table) {
        const { page, perPage, orderDirection, orderBy } = dataTableParams;
        params.page = page.toString();
        params.per_page = perPage.toString();
        if (orderBy) {
            params.order_direction = orderDirection.toString();
            params.order_by = orderBy.toString();
        }
    }
    if (dataTableQueryParams) {
        Object.entries(dataTableQueryParams).forEach(([key, value]) => {
            if (value) {
                params[key] = value;
            } else {
                delete params[key];
            }
        });
    }
    // AI list endpoints only accept pagination and their dedicated filters.
    const isAiFeedback = eventType === EventType.aiFeedback;
    if (eventType === EventType.aiChat || isAiFeedback) {
        const { search, reaction } = state.insights[eventType];
        if (search) {
            params.search = search;
        }

        if (isAiFeedback && reaction) {
            params.reaction = reaction;
        }
        return pick(AI_LIST_PARAMS, params);
    }
    return params;
};
